import React from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from "react-router-dom";
import "./index.css";

const EMAIL = "osacoach.ia@gmail.com";
const STRIPE_SINGLE = "https://buy.stripe.com/aFaaEQ6t12x41tWeCj4ZG00"; // your Stripe Payment Link for $129


async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
}

function CopyEmail({ style }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      className="btn outline"
      style={style}
      onClick={async () => {
        await copyToClipboard(EMAIL);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      title="Copy email to clipboard"
    >
      {copied ? "Copied!" : "Copy email"}
    </button>
  );
}

const NavItem = ({ to, children }) => (
  <NavLink className="nav" to={to} style={({ isActive }) => ({
    textDecoration: "none"
  })} aria-current={({ isActive }) => isActive ? "page" : undefined}>
    {children}
  </NavLink>
);

function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="kicker">Coaching</div>
          <h1>Coaching for clarity, growth, and confident career transitions.</h1>
          <p>Less noise. More signal. Coaching that helps you focus on what actually moves the needle — with structure, accountability, and momentum.</p>
          <p style={{ color: "var(--muted)" }}>Available in English & Russian.</p>
          <a className="btn" href={STRIPE_SINGLE} target="_blank" rel="noreferrer">Book a Session</a>
        </div>
      </section>

      {/* Why Work With Me */}
      <section className="section">
        <div className="container">
          <div className="kicker">Why Work With Me</div>
          <div className="grid">
            <div className="card">
              <h3>Focused sessions</h3>
              <p>Tailored to your goals — not generic frameworks.</p>
            </div>
            <div className="card">
              <h3>Structure & accountability</h3>
              <p>Progress tracked, clarity at every step.</p>
            </div>
            <div className="card">
              <h3>Results-driven approach</h3>
              <p>Less noise, more signal.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section">
        <div className="container">
          <div className="kicker">How It Works</div>
          <div className="steps">
            <div className="step">
              <strong className="num">1</strong>
              <div>
                <h3>Book a time that works for you.</h3>
                <p>Your schedule, your pace.</p>
              </div>
            </div>
            <div className="step">
              <strong className="num">2</strong>
              <div>
                <h3>Define your goals and constraints together.</h3>
                <p>Clarity first — so every session has direction.</p>
              </div>
            </div>
            <div className="step">
              <strong className="num">3</strong>
              <div>
                <h3>Execute a simple plan with check-ins.</h3>
                <p>Practical steps, consistent progress, measurable results.</p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: "1.25rem", display: "flex", gap: ".75rem", alignItems: "center" }}>
            <NavLink className="btn outline" to="/about">Learn more about me → About</NavLink>
            <a className="btn" href={STRIPE_SINGLE} target="_blank" rel="noreferrer">Book a Session</a>
          </div>
        </div>
      </section>
    </div>
  );
}

function About() {
  return (
    <div className="section">
      <div className="container about-layout">
        {/* Left: Photo */}
        <div className="card" style={{ alignSelf: "start", textAlign: "center" }}>
          <div className="about-photo">
            <img src="/headshot.jpg" alt="Portrait of Tatsiana Darazhko" />
          </div>
        </div>

        {/* Right: Content */}
        <div>
          <div className="kicker">About</div>
          <h2>Tatsiana Darazhko <span className="submark">OSA</span></h2>

          <ul>
            <li>Product Owner / Product Manager / Lead Business Analyst, leading cross-functional, cross-cultural in-house and vendor teams at a U.S. tax and consulting firm.</li>
            <li>Relocated internationally and built a career in the U.S. tech & consulting market, navigating cultural shifts and competitive job searches.</li>
            <li>10+ years in product & business analysis across outsourcing, consulting, and in-house roles.</li>
            <li>Experience spanning local startups to global corporations, from mobile apps to highly regulated industries (healthcare, tax).</li>
            <li>Proven track record with large-scale collaboration platforms, including integrations, data automation, and document & task management.</li>
          </ul>

          <h3 className="kicker" style={{ marginTop: "2rem" }}>Outside of work</h3>
          <p>
            My career is in product and consulting, but coaching is where I get to give back — helping others grow through the transitions I’ve experienced myself.
            And when I need perspective, I head outdoors: exploring national parks, hiking ultra trails, or running long distances.
          </p>
        </div>
      </div>
    </div>
  );
}


function Coaching() {
  return (
    <div className="section">
      <div className="container">
        <div className="kicker">Coaching</div>
        <h2>Every transition is a chance to grow stronger and aim higher.</h2>
        <p className="lead">
          I coach Business Analysts building careers in consulting and product roles, especially those adapting to new environments,
          new responsibilities, and new countries.
        </p>

        {/* Prominent CTAs: LinkedIn highlighted up top */}
        <div
          className="cta-row"
          style={{ display: "flex", gap: ".75rem", flexWrap: "wrap", marginTop: ".75rem" }}
        >
          <a
            className="btn"
            href={STRIPE_SINGLE}
            target="_blank"
            rel="noreferrer"
            aria-label="Book a coaching session "
          >
            Book a Session
          </a>
          <a
            className="btn outline"
            href="https://www.linkedin.com/in/tatsiana-darazhko/?locale=en_US"
            target="_blank"
            rel="noreferrer"
            aria-label="Tatsiana Darazhko on LinkedIn"
            title="Prefer to connect first? Say hi on LinkedIn."
          >
            Connect on LinkedIn
          </a>
        </div>

        {/* What I coach */}
        <div className="card" style={{ marginTop: "1rem" }}>
          <h3>What I coach (topics & outcomes)</h3>
          <ul>
            <li>
              <strong>Transitions & career strategy</strong> → outsourcing → in-house, BA → PO/PM, stepping into consulting.
            </li>
            <li>
              <strong>Communication & influence</strong> → turning BA challenges into opportunities to lead — managing stakeholders, aligning teams, and influencing without authority.
            </li>
            <li>
              <strong>Decision-making in complexity</strong> → navigating conflict, prioritization, and trade-offs with clarity.
            </li>
            <li>
              <strong>Relocation & adaptation</strong> → thriving in new environments by embracing cross-cultural dynamics and understanding how expectations change from one country, company, or project to another.
            </li>
            <li>
              <strong>Clarity & momentum</strong> → simple systems for goals, habits, and accountability that stick.
            </li>
          </ul>
        </div>

        {/* Who this is for */}
        <div className="card" style={{ marginTop: "1rem" }}>
          <h3>Who this is for</h3>
          <ul>
            <li>Business Analysts at any stage of their career.</li>
            <li>Professionals considering or already moving into product management.</li>
            <li>Anyone navigating big transitions — new roles, new environments, or new countries.</li>
          </ul>
          <p style={{ color: "var(--muted)", marginTop: ".5rem" }}>
            I coach in English and Russian, working with clients across the globe.
          </p>
        </div>

        {/* Not a fit */}
        <div className="card" style={{ marginTop: "1rem" }}>
          <h3>Not a fit if you want</h3>
          <ul>
            <li>Therapy or mental health counseling (I’m not a therapist).</li>
            <li>“Quick hacks” without doing the reflective work.</li>
            {/* removed: purely technical coaching */}
          </ul>
        </div>

        {/* Approach */}
        <div className="card" style={{ marginTop: "1rem" }}>
          <h3>My approach</h3>
          <ul>
            <li>
              <strong>Clarify</strong> → Define goals, constraints, and context.
            </li>
            <li>
              <strong>Structure</strong> → Apply simple frameworks to cut through complexity.
            </li>
            <li>
              <strong>Momentum</strong> → Consistent steps forward with accountability.
            </li>
          </ul>
          <p style={{ marginTop: ".5rem" }}>
            This is for when you’re ready to stop circling decisions and start moving — less noise, more signal.
          </p>
        </div>

{/* Pricing (pay first → then book) */}
<div className="card" style={{ marginTop: "1rem" }}>
  <h3>Pricing</h3>
  <p><strong>60-minute session — $129</strong></p>

  <div style={{ display: "flex", gap: ".75rem", flexWrap: "wrap", marginTop: ".5rem" }}>
    <a
      className="btn"
      href={STRIPE_SINGLE}
      target="_blank"
      rel="noreferrer"
      aria-label="Pay $129"
    >
      Pay $129
    </a>
    <a
      className="btn outline"
      href="https://calendly.com/tdarazhko"
      target="_blank"
      rel="noreferrer"
      aria-label="Then book on Calendly"
    >
      Then book on Calendly
    </a>
  </div>

  <p className="muted" style={{ marginTop: 8 }}>
    After payment you’ll be redirected to Calendly to pick a time. If not, use the button above and
    enter your <strong> Stripe receipt ID</strong> on the booking form.
  </p>
</div>

          <p style={{ marginTop: 12 }}>Available in English &amp; Russian.</p>
        </div>
      </div>
    );
}

/*
function Store() {
  return (
    <div className="section">
      <div className="container">
        <div className="kicker">Store</div>
        <h2>Coming soon</h2>
        <p>Digital books and video lectures. Elegant, practical, minimal.</p>
        <div className="grid">
          <div className="card">
            <h3>Book — TBA</h3>
            <p>A short, actionable guide to focused growth.</p>
            <span className="btn outline" aria-disabled="true">Coming Soon</span>
          </div>
          <div className="card">
            <h3>Video Lecture — TBA</h3>
            <p>Clarity-first frameworks you can apply immediately.</p>
            <span className="btn outline" aria-disabled="true">Coming Soon</span>
          </div>
        </div>
      </div>
    </div>
  );
}
*/

function Contact() {
  return (
    <div className="section">
      <div className="container">
        <div className="kicker">Contact</div>
        
        <p>
          Email: <strong className="mono">{EMAIL}</strong>
        </p>

        {/* Copy-to-clipboard button */}
        <CopyEmail />

        {/* Optional: keep a fallback mailto link as a small secondary action */}
        {/* <a
          className="btn outline"
          href={`mailto:${EMAIL}?subject=Coaching%20Inquiry%20—%20Tatsiana%20Darazhko`}
          style={{ marginLeft: 12 }}
        >
          Open email app
        </a> */}
      </div>
    </div>
  );
}
export default function App() {
  return (
    <Router>
      <nav>
        <div className="nav-inner">
          <div className="logo">
            Tatsiana Darazhko
          <span className="submark" aria-label="Mark">OSA</span></div>
          <NavItem to="/">Home</NavItem>
          <NavItem to="/about">About</NavItem>
          <NavItem to="/coaching">Coaching</NavItem>
          {/* <NavItem to="/store">Store</NavItem> */}
          <NavItem to="/contact">Contact</NavItem>
          <a className="btn" href={STRIPE_SINGLE} target="_blank" rel="noreferrer">Book</a>
        </div>
      </nav>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/coaching" element={<Coaching />} />
        {/* <Route path="/store" element={<Store />} /> */}
        <Route path="/store" element={<Navigate to="/coaching" replace />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
       <footer>  © {new Date().getFullYear()} Tatsiana Darazhko — All rights reserved.</footer>
    </Router>
  );
}
